import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import Modal from "../../../components/base/Modal";
import { Badge, Button, Select } from "../../../components/ui";
import {
  useCentinelaCatalogs,
  useCentinelaTripCustody,
  useCentinelaUsers,
} from "../../../features/centinela/queries";
import {
  useEndCentinelaTripCustody,
  useUpdateCentinelaTripCustody,
} from "../../../features/centinela/mutations";
import { CENTINELA_CUSTODY_THRESHOLDS } from "../../../features/centinela/types";
import type {
  CentinelaCustodyThreshold,
  CentinelaTripCustody,
} from "../../../features/centinela/types";
import type { Trip } from "../../../store/admin/tripsStore";
import {
  CUSTODY_THRESHOLD_OPTIONS,
  CUSTOM_CUSTODY_PROFILE,
  custodyUnitLabelKey,
  hasCustodyChanges,
  isPresetProfile,
  mapCentinelaTripCustody,
  mapCustodyParameters,
  mapCustodyProfiles,
  matchCustodyProfile,
  toTripCustodyInput,
  withServedOption,
} from "../services/mapCentinelaCustody";
import { mapCentinelaUsers } from "../services/mapCentinelaUsers";
import { custodyProfileMeta } from "../theme";
import CustodyAlertsCoverage, { TRIP_ALERT_SCOPES } from "./CustodyAlertsCoverage";
import RecipientPicker from "./RecipientPicker";
import TabLoader from "./TabLoader";

export interface TripCustodyModalProps {
  trip: Trip;
  onClose: () => void;
}

export default function TripCustodyModal({ trip, onClose }: TripCustodyModalProps) {
  const { t } = useTranslation("common");

  const custodyQuery = useCentinelaTripCustody(trip.id);
  const catalogsQuery = useCentinelaCatalogs();
  const usersQuery = useCentinelaUsers();
  const updateCustody = useUpdateCentinelaTripCustody();
  const endCustody = useEndCentinelaTripCustody();

  const [draft, setDraft] = useState<CentinelaTripCustody | null>(null);
  const [picked, setPicked] = useState<string | null>(null);

  const served = useMemo(
    () => mapCentinelaTripCustody(custodyQuery.data),
    [custodyQuery.data],
  );

  const custody = draft ?? served;

  const recipients = useMemo(
    () => mapCentinelaUsers(usersQuery.data, () => null).filter((user) => user.active),
    [usersQuery.data],
  );

  const tripAlerts = useMemo(
    () =>
      (catalogsQuery.data?.alert_types ?? []).filter((alert) =>
        TRIP_ALERT_SCOPES.includes(alert.scope),
      ),
    [catalogsQuery.data],
  );

  const profiles = useMemo(
    () => mapCustodyProfiles(catalogsQuery.data),
    [catalogsQuery.data],
  );

  const thresholdFields = useMemo(() => {
    const served = mapCustodyParameters(catalogsQuery.data);
    if (served.length > 0) return served;

    return CENTINELA_CUSTODY_THRESHOLDS.map((field) => ({
      field,
      label: "",
      unit: "",
      description: "",
    }));
  }, [catalogsQuery.data]);

  const activeProfile = picked ?? matchCustodyProfile(profiles, custody) ?? CUSTOM_CUSTODY_PROFILE;

  const thresholdsLocked = isPresetProfile(profiles, activeProfile);

  const coveredAlerts =
    profiles.find((profile) => profile.id === activeProfile)?.alerts ?? null;

  const pickProfile = (profileId: string) => {
    const profile = profiles.find((item) => item.id === profileId);
    setPicked(profileId);
    if (profile?.thresholds) setDraft({ ...custody, ...profile.thresholds });
  };

  const updateThreshold = (key: CentinelaCustodyThreshold, value: number) => {
    setPicked(CUSTOM_CUSTODY_PROFILE);
    setDraft({ ...custody, [key]: value });
  };

  const changeRecipients = (userIds: string[]) => {
    setDraft({ ...custody, notify_user_ids: userIds });
  };

  const blocker =
    custody.notify_user_ids.length === 0
      ? "monitoreoIa.units.custodyModal.needRecipients"
      : null;

  const save = () => {
    updateCustody
      .mutateAsync({
        tripId: trip.id,
        input: toTripCustodyInput(served, custody, profiles, activeProfile),
      })
      .then(() => {
        toast.success(
          t("monitoreoIa.trips.custodyModal.saved", {
            trip: trip.title ?? t("monitoreoIa.trips.untitled"),
          }),
        );
        onClose();
      })
      .catch(() => {
        toast.error(t("monitoreoIa.units.custodyModal.saveError"));
      });
  };

  const end = () => {
    endCustody
      .mutateAsync(trip.id)
      .then(() => {
        toast.success(t("monitoreoIa.units.custodyModal.tripEnded"));
        onClose();
      })
      .catch(() => {
        toast.error(t("monitoreoIa.units.custodyModal.saveError"));
      });
  };

  return (
    <Modal
      onClose={onClose}
      title={t("monitoreoIa.trips.custodyModal.title")}
      width="w-full max-w-5xl"
      content={
        custodyQuery.isLoading ? (
          <div className="flex min-h-[240px] items-center justify-center">
            <TabLoader label={t("monitoreoIa.units.custodyModal.loading")} />
          </div>
        ) : (
          <div className="space-y-5">
            <p className="text-[11.5px] text-content-secondary">
              {[trip.title, trip.route?.name].filter(Boolean).join(" · ")}
            </p>

            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[12px] font-semibold text-content">
                  {t("monitoreoIa.trips.custodyModal.profileLabel")}
                </p>
                <Badge tone="info" size="sm">
                  {t(`monitoreoIa.trips.custodyModal.profiles.${activeProfile}.label`, {
                    defaultValue:
                      profiles.find((profile) => profile.id === activeProfile)?.name ??
                      activeProfile,
                  })}
                </Badge>
              </div>

              <p className="mb-3 text-[11px] leading-relaxed text-content-secondary">
                {t("monitoreoIa.trips.custodyModal.profileHint")}
              </p>

              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {profiles.map((profile) => {
                  const meta = custodyProfileMeta(profile.id);
                  const isActive = activeProfile === profile.id;

                  return (
                    <button
                      key={profile.id}
                      type="button"
                      onClick={() => pickProfile(profile.id)}
                      className={`flex flex-col items-start gap-2 rounded-xl border p-3 text-left transition-colors max-sm:min-h-16 max-sm:p-4 ${
                        isActive
                          ? "border-primary-500 bg-primary-500/5"
                          : "border-line bg-card hover:border-primary-500/40"
                      }`}
                    >
                      <span className="flex w-full items-center gap-2">
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                            isActive ? "bg-primary-500 text-on-primary" : meta.chip
                          }`}
                        >
                          <i className={`${meta.icon} text-xs`} />
                        </span>
                        <span className="text-[12px] font-semibold text-content">
                          {t(`monitoreoIa.trips.custodyModal.profiles.${profile.id}.label`, {
                            defaultValue: profile.name,
                          })}
                        </span>
                        {isActive ? (
                          <i className="ri-check-line ml-auto text-xs text-primary-500" />
                        ) : null}
                      </span>

                      <span className="text-[10.5px] leading-snug text-content-secondary max-sm:text-xs">
                        {t(`monitoreoIa.trips.custodyModal.profiles.${profile.id}.description`, {
                          defaultValue: profile.description,
                        })}
                      </span>

                      {profile.callsPer4h === null ? null : (
                        <span className="flex items-center gap-1 text-[10px] font-medium text-warning">
                          <i className="ri-phone-line text-[11px]" />
                          {t("monitoreoIa.units.custodyModal.callsPer4h", {
                            count: profile.callsPer4h,
                          })}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2 rounded-xl border border-line bg-surface p-3">
              <p className="text-[11.5px] font-semibold text-content">
                {t("monitoreoIa.trips.custodyModal.thresholdsLabel")}
              </p>

              {thresholdsLocked ? (
                <p className="flex items-start gap-2 text-[10.5px] leading-relaxed text-content-secondary">
                  <i className="ri-lock-line mt-0.5 shrink-0 text-[11px]" />
                  {t("monitoreoIa.units.custodyModal.thresholdsLocked")}
                </p>
              ) : null}

              <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                {thresholdFields.map(({ field, label, unit, description }) => {
                  const unitKey = custodyUnitLabelKey(unit);

                  return (
                    <Select
                      key={field}
                      size="sm"
                      disabled={thresholdsLocked}
                      title={description || undefined}
                      label={t(`monitoreoIa.units.custodyModal.thresholds.${field}`, {
                        defaultValue: label || field,
                      })}
                      value={`${custody[field]}`}
                      onChange={(event) => updateThreshold(field, Number(event.target.value))}
                      options={withServedOption(
                        CUSTODY_THRESHOLD_OPTIONS[field],
                        custody[field],
                      ).map((value) => ({
                        value: `${value}`,
                        label: unitKey ? t(unitKey, { value }) : `${value} ${unit}`.trim(),
                      }))}
                    />
                  );
                })}
              </div>
            </div>

            <RecipientPicker
              users={recipients}
              selected={custody.notify_user_ids}
              loading={usersQuery.isLoading}
              label={t("monitoreoIa.units.custodyModal.recipientsLabel")}
              emptyLabel={t("monitoreoIa.units.custodyModal.recipientsEmpty")}
              onChange={changeRecipients}
            />

            <CustodyAlertsCoverage alerts={tripAlerts} covered={coveredAlerts} />

            {blocker ? (
              <p className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-2.5 py-2 text-[11px] leading-relaxed text-warning">
                <i className="ri-alert-line mt-0.5 shrink-0 text-xs" />
                {t(blocker)}
              </p>
            ) : null}

            <div className="flex flex-col gap-2 border-t border-line pt-4 sm:flex-row">
              <Button variant="secondary" size="sm" fullWidth className="max-sm:min-h-11" onClick={onClose}>
                {t("common.cancel")}
              </Button>

              <Button
                size="sm"
                fullWidth
                className="max-sm:min-h-11"
                disabled={Boolean(blocker) || updateCustody.isPending}
                loading={updateCustody.isPending}
                onClick={save}
                icon={<i className="ri-shield-flash-line" />}
              >
                {hasCustodyChanges(served, custody)
                  ? t("monitoreoIa.units.custodyModal.activateWithChanges")
                  : t("monitoreoIa.units.custodyModal.activate")}
              </Button>

              {served.profile ? (
                <Button
                  variant="dangerSoft"
                  size="sm"
                  fullWidth
                  className="max-sm:min-h-11"
                  disabled={endCustody.isPending}
                  loading={endCustody.isPending}
                  onClick={end}
                  icon={<i className="ri-shield-cross-line" />}
                >
                  {t("monitoreoIa.units.custodyModal.tripEnd")}
                </Button>
              ) : null}
            </div>
          </div>
        )
      }
    />
  );
}
